import { useCallback, useEffect, useReducer, useRef } from "react";
import i18n from "../../language/i18n";
import Icon from "../../library/components/icons/Icon";
import { retrieveAICreditStatus } from "../../requests/AIRequests";
import {
  AiCreditStatus,
  resolveLimitFreesAt,
} from "../../utilization/AiCreditUtilization";
import {
  AI_REFUSAL_EVENT,
  AI_REFUSAL_RESET_EVENT,
  AiRefusalDetail,
  dispatchAiPoolFreed,
} from "../../utilization/AiRefusalUtilization";
import {
  INITIAL_AI_REFUSAL_NOTICE_STATE,
  isAiRefusalNoticeVisible,
  reduceAiRefusalNotice,
  AiRefusalNoticeEvent,
  AiRefusalNoticeState,
} from "../../utilization/AiRefusalNoticeModel";
import LiveCountdown from "./LiveCountdown";
import { aiRefusalNoticeStyles } from "./aiRefusalNoticeStyles";

/**
 * The band that tells a learner their AI help is out of budget, and when it comes back.
 *
 * The chat already says something in the bubble when a hint or a solution is refused, and that message
 * stays. What it cannot say is WHEN — the 423 carries only the feature's error key, and no Retry-After —
 * so a learner was left with "come back later" and no idea whether that meant minutes or days. This band
 * is the half that answers it, and it counts down so the answer stays true while it is on screen.
 *
 * It costs nothing until something is refused: no fetch on mount, no polling.
 *
 * Every decision lives in `reduceAiRefusalNotice`, which is tested directly; this component owns only
 * the effects — the credit read, the countdown handoff and the toasts.
 */
const AiRefusalNotice = () => {
  const [state, dispatchNotice] = useReducer(
    (current: AiRefusalNoticeState, event: AiRefusalNoticeEvent) =>
      reduceAiRefusalNotice(current, event).state,
    INITIAL_AI_REFUSAL_NOTICE_STATE
  );

  const isMountedRef = useRef(true);

  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  const readCreditStatus = useCallback(async () => {
    const { data, err } = await retrieveAICreditStatus();
    if (!isMountedRef.current) return;
    if (err || !data) {
      // Best-effort by design: this endpoint can itself answer 423 for a token with no profile pair.
      // The refusal was still real, so the band appears without a time rather than not at all.
      dispatchNotice({ type: "statusFailed" });
      return;
    }
    const { freesAt } = resolveLimitFreesAt(data as AiCreditStatus);
    if (freesAt === null) {
      // The server named no blocked window, so the budget is back. This is the ONLY place that learns
      // it, and other surfaces need to know: a diagram that was refused caches that refusal, and
      // without this signal it would keep serving it for the rest of the session.
      dispatchAiPoolFreed();
    }
    dispatchNotice({ type: "statusResolved", freesAt });
  }, []);

  // The machine asking to read the status IS the "fetching" phase, so the fetch is driven by the phase
  // rather than by a stashed effect flag. `lastFetchedAt` is in the deps because a second read (after
  // the cooldown, or when a reset moment passes) re-enters the same phase and must not be skipped.
  useEffect(() => {
    if (state.phase !== "fetching") return;
    readCreditStatus();
  }, [state.phase, state.lastFetchedAt, readCreditStatus]);

  useEffect(() => {
    const handleRefusal = (event: Event) => {
      const detail = (event as CustomEvent<AiRefusalDetail>).detail;
      if (detail?.kind !== "limit") {
        // ONLY the spent budget is this component's business.
        //
        // A busy model (429) and spent points (406) are already said where they happened: all seven
        // chat surfaces put their own sentence in the bubble for exactly these, and the report card
        // toasts its own. An "ambient" copy meant the same message twice for one tap — and on the
        // report card two DIFFERENT ones, since its fallback is the generic "geri bildirim
        // oluşturulamadı". Neither is ambient, and both clear on their own.
        return;
      }
      dispatchNotice({ type: "limitRefusal" });
    };
    // The band is per-session state — this learner's budget and reset time — so it clears when the
    // session does. Without it a same-tab logout carried the band onto the login screen, where its
    // timer could still fire a credit read after the interceptors were ejected.
    const handleReset = () => dispatchNotice({ type: "reset" });
    window.addEventListener(AI_REFUSAL_EVENT, handleRefusal);
    window.addEventListener(AI_REFUSAL_RESET_EVENT, handleReset);
    return () => {
      window.removeEventListener(AI_REFUSAL_EVENT, handleRefusal);
      window.removeEventListener(AI_REFUSAL_RESET_EVENT, handleReset);
    };
  }, []);

  const handleCountdownComplete = useCallback(() => {
    dispatchNotice({ type: "tick" });
  }, []);

  // With a reset time, LiveCountdown owns the clock and fires `onComplete` exactly once at zero, so no
  // second timer is needed. Without one, this is the only thing that retires the band.
  //
  // `state.tickSeq` is in the deps because this timer is ONE-SHOT and re-armed from state: a tick that
  // fires early (a backward clock step, resume from suspend) leaves every other field identical, so
  // without it the effect never tears down, no replacement is scheduled, and the band strands.
  //
  // Armed only in the two phases whose `tick` has a branch that acts. While fetching, the deadline that
  // triggered the re-check is already past, so the delay would be 0 and — now that every tick changes
  // state identity — the effect would re-arm itself every few milliseconds for the whole read.
  useEffect(() => {
    if (state.phase !== "shown" && state.phase !== "dismissed") return;
    if (state.phase === "shown" && state.freesAt !== null) return;
    // Phase-specific: a dismissed band can still carry the `freesAt` of the band it replaced, and on
    // the dismiss-during-re-check path that moment is in the past.
    const deadline =
      state.phase === "dismissed" ? state.suppressedUntil : state.autoHideAt;
    if (deadline === null) return;
    const id = window.setTimeout(
      () => dispatchNotice({ type: "tick" }),
      Math.max(deadline - Date.now(), 0)
    );
    return () => window.clearTimeout(id);
  }, [
    state.phase,
    state.freesAt,
    state.autoHideAt,
    state.suppressedUntil,
    state.tickSeq,
  ]);

  const isVisible = isAiRefusalNoticeVisible(state);

  // Reserve the space only while the band is up, and give it back on unmount — otherwise every screen
  // keeps a 44px hole at the top. Same mechanism as ImpersonationBanner, and the two compose in CSS.
  useEffect(() => {
    const root = document.documentElement;
    if (isVisible) {
      root.classList.add("arb-offset");
    } else {
      root.classList.remove("arb-offset");
    }
    return () => root.classList.remove("arb-offset");
  }, [isVisible]);

  if (!isVisible) return null;

  return (
    <>
      <style>{aiRefusalNoticeStyles}</style>
      <div className="arb-bar" role="status" aria-live="polite">
        <span className="arb-icon" aria-hidden="true">
          <Icon name="warning" size={16} />
        </span>
        <span className="arb-text">
          <span className="arb-title">{i18n.t("ai_limit_banner_title")}</span>
          <span className="arb-detail">{i18n.t("ai_limit_banner_no_time")}</span>
        </span>
        {state.freesAt !== null ? (
          <span className="arb-reset">
            <LiveCountdown
              // Keyed on the target so a NEW reset time gets a NEW instance. LiveCountdown fires
              // onComplete exactly once per mount (`firedRef`) and never resets it when `targetAt`
              // changes — so when a re-check hands it a later moment (the hourly window rolled over but
              // the weekly one is still blocking), the same instance would sit at zero forever and the
              // band could never retire itself.
              key={state.freesAt}
              targetAt={state.freesAt}
              variant="compact"
              prefixKey="ai_limit_banner_resets_in"
              onComplete={handleCountdownComplete}
            />
          </span>
        ) : null}
        <button
          type="button"
          className="arb-dismiss"
          aria-label={i18n.t("close")}
          title={i18n.t("close")}
          onClick={() => dispatchNotice({ type: "dismiss" })}
        >
          <Icon name="close" size={12} />
        </button>
      </div>
    </>
  );
};

export default AiRefusalNotice;
